import { UnauthorizedError } from '@getvim-os/errors';
import { SETTINGS_SDK } from '@getvim/vim-app-settings-sdk-api';
import {
  AppHandshakeError,
  AppToOSMessageTypes,
  HandshakeMetadata,
  HandshakePayload,
  OAuthHandshakePayload,
  OSToAppMessageEvent,
  OSToAppMessageTypes,
} from '@getvim/vim-os-api';
import { EHR, GENERIC_SDK } from '@getvim/vim-os-sdk-api';
import 'reflect-metadata';
import { getSdkMetadata, SdkMetadata } from './metadata';
import { CommonVimSDK, OsToExternalAppEhrConvertor } from './sdk';
import { SdkLogger } from './sdkLogger';
import { InitializeVimSDKOptions } from './types';
import { WindowMethodBlocker } from './windowMethodBlocker';
import { CapabilityMonitor } from './capabilityMonitor';

type EHR_STATE = EHR.AppEhrState<{ withPII: true }>;

type SDK = GENERIC_SDK<EHR.EHR_STATE_TEMPLATE> | SETTINGS_SDK;

export { SdkLogger } from './sdkLogger';

export class ExternalSDK extends CommonVimSDK<EHR_STATE> {
  constructor(
    payload: HandshakePayload,
    osMessageChannel: MessageChannel,
    logger: SdkLogger,
    tokensResponse?: { idToken: string },
  ) {
    super({
      payload,
      osMessageChannel,
      oSToAppEhrConvertor: new OsToExternalAppEhrConvertor(),
      logger,
      tokensResponse,
    });
  }
}

let sdkPromise: Promise<SDK> | undefined;
let sdk: SDK | undefined;

const logger = new SdkLogger();

// Create blocker instance and start with logging only (don't block yet)
// Will block after handshake if feature flag indicates blocking is needed
const windowMethodBlocker = new WindowMethodBlocker(window, logger);
windowMethodBlocker.setBlockState(false);

// Initialize capability monitoring to track API usage
const capabilityMonitor = new CapabilityMonitor(window, logger);
capabilityMonitor.initialize();

export const initializeVimSDK = <SDKImpl extends SDK = ExternalSDK>(
  options: InitializeVimSDKOptions<SDKImpl> = {},
): Promise<SDKImpl> => {
  if (!sdkPromise) {
    sdkPromise = new Promise<SDKImpl>((resolve, reject) => {
      (async (logger) => {
        let sdkMetadata: SdkMetadata = {};
        try {
          sdkMetadata = getSdkMetadata();
        } catch (error) {
          logger.error('Failed to get SDK metadata', { error });
        }
        const appHandshakeTraceId = Math.random().toString(16).slice(2);

        let appUrl: string | undefined;
        try {
          const appUrlObject = new URL(window.location.href);
          appUrlObject.search = '';
          appUrl = appUrlObject.href;
        } catch {
          /* empty */
        }

        logger.updateMetadata({
          ...sdkMetadata,
          appHandshakeTraceId,
          appUrl,
        });

        logger.info('Starting application handshake with vim-os.', {
          sdkMetadata,
          appHandshakeTraceId,
          appUrl,
        });

        const { accessToken, idToken } = (await generateAppTokens(options, logger)) || {};

        const messageChannel = new MessageChannel();
        messageChannel.port1.addEventListener('message', (message: OSToAppMessageEvent) => {
          switch (message?.data?.type) {
            case OSToAppMessageTypes.APP_HANDSHAKE_ACK: {
              const handshakePayload = message.data.payload;

              const {
                manifestSupport,
                organization,
                bareboneType,
                bareboneVersion,
                hostname,
                allowWindowModals,
              } = handshakePayload;

              logger.info('Got handshake response from vim-os.', {
                handshakePayload: {
                  manifestSupport,
                  organization,
                  bareboneVersion,
                  bareboneType,
                  hostname,
                  allowWindowModals,
                },
              });

              // If feature flag does NOT allow window modals, block them
              // (We started with logging only, now block if needed)
              if (allowWindowModals === false) {
                logger.info('window modals should be blocked - applying blocking');
                windowMethodBlocker.setBlockState(true);
              } else {
                logger.info('window modals allowed - keeping logging only mode');
                // Methods remain in log-only mode (already set at module load)
              }

              const tokensResponse = idToken ? { idToken } : undefined;

              sdk = options.sdkFactory
                ? options.sdkFactory(handshakePayload, messageChannel, logger, tokensResponse)
                : new ExternalSDK(handshakePayload, messageChannel, logger, tokensResponse);
              resolve(sdk as SDKImpl);
              break;
            }
            case OSToAppMessageTypes.HEALTH_CHECK: {
              message.ports[0].postMessage({
                error: false,
              });
              break;
            }
            case OSToAppMessageTypes.APP_HANDSHAKE_ERROR: {
              logger.error('Recived error in handshake.', {
                handshakeErrorPayload: message.data.payload,
              });

              reject(message.data.payload);
              break;
            }
          }
        });
        messageChannel.port1.start();

        if (isUsingNonVersionedSDK(sdkMetadata)) {
          sdkMetadata.isUsingNonVersionedSdk = true;
          // eslint-disable-next-line no-console
          console.info(
            "It looks like you're using an unversioned URL for the Vim OS SDK script tag. For better stability and compatibility, please use a versioned URL.\n" +
              'A versioned URL should look like this: https://connect.getvim.com/vim-os-sdk/v0.0.x/vim-sdk.js\n' +
              'If you are using NPM, upgrading your package should resolve this issue.\n' +
              'For more information, please refer to the documentation: https://docs.getvim.com/vim-os-js/getting-started.html',
          );
        }

        const handshakeMetadata: HandshakeMetadata = {
          ...sdkMetadata,
          appHandshakeTraceId,
          appUrl,
        };

        if (!accessToken) {
          logger.info('Sending application handshake initial message - legacy flow.');
          window.parent.postMessage(
            {
              type: AppToOSMessageTypes.APP_HANDSHAKE,
              payload: {
                metadata: handshakeMetadata,
              },
            },
            '*',
            [messageChannel.port2],
          );
        } else {
          logger.info('Sending application handshake initial message - oAuth flow.');
          const payload: OAuthHandshakePayload = {
            token: accessToken,
            metadata: handshakeMetadata,
          };
          window.parent.postMessage(
            {
              type: AppToOSMessageTypes.APP_OAUTH_HANDSHAKE,
              payload,
            },
            '*',
            [messageChannel.port2],
          );
        }
      })(logger).catch((error) => {
        logger.error('Error initializing sdk.', { error });

        if (error instanceof UnauthorizedError) {
          const payload: AppHandshakeError = {
            code: 'authorization_error',
            data: error.message,
          };

          window.parent.postMessage(
            {
              type: AppToOSMessageTypes.APP_HANDSHAKE_ERROR,
              payload,
            },
            '*',
          );
        } else {
          const payload: AppHandshakeError = {
            code: 'internal_error',
            data: error.toString(),
          };

          window.parent.postMessage(
            {
              type: AppToOSMessageTypes.APP_HANDSHAKE_ERROR,
              payload,
            },
            '*',
          );
        }

        reject(error);
      });
    });
  }
  if (sdk) {
    logger.info('SDK already initialized, returning existing instance.');
    return Promise.resolve(sdk as SDKImpl);
  }
  logger.info('Initializing SDK');
  return sdkPromise as Promise<SDKImpl>;
};

const safeParseResponse = async (response: Response): Promise<TokenResponse> => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const convertTokenResponse = ({
  access_token,
  accessToken,
  token,
  idToken,
  id_token,
}: TokenResponse): { accessToken?: string; idToken?: string; token?: string } => {
  return { accessToken: access_token || accessToken || token, idToken: idToken || id_token };
};

const generateAppTokens = async (
  options: InitializeVimSDKOptions<any>,
  logger: SdkLogger,
): Promise<{ accessToken: string; idToken?: string } | undefined> => {
  if (options.accessToken) {
    return { accessToken: options.accessToken };
  }

  const code = new URL(window.location.href).searchParams.get('code');
  const encodedTokenEndpoint = new URL(window.location.href).searchParams.get('token_endpoint');
  const accessTokenStorageKey = `vim-os_app-accesstoken_${code}`;
  const idTokenStorageKey = `vim-os_app-idtoken_${code}`;
  try {
    const accessToken = window.sessionStorage.getItem(accessTokenStorageKey);
    const idToken = window.sessionStorage.getItem(idTokenStorageKey) || undefined;

    if (accessToken) {
      logger.info('Using existing cached app token.');
      return { accessToken, idToken };
    }
  } catch {
    /* empty */
  }

  if (!code) {
    logger.info('No auth code found, App working in legacy mode');
    return;
  }

  if (!options.appTokenEndpoint && !encodedTokenEndpoint) {
    logger.error('sdk initialized without token endpoint');
    throw new Error('Token endpoint is required, please define it in application manifest.');
  }

  let response: Response;
  try {
    const appTokenEndpoint = new URL(
      options.appTokenEndpoint
        ? options.appTokenEndpoint
        : decodeURIComponent(encodedTokenEndpoint as string),
    );
    response = await fetch(appTokenEndpoint.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
      }),
    });
  } catch (error_) {
    const error = error_ as Error;
    logger.error('Failed to get app token', { appTokenEndpoint: options.appTokenEndpoint }, error);
    throw new Error(`Failed to get app token: ${error?.message || 'unknown network error'}`);
  }

  const result = await safeParseResponse(response);
  if (response.status >= 300 || response.status < 200) {
    logger.error('Failed to get app token', {
      text: response.statusText,
      status: response.status,
      result,
    });
    if (response.status === 401 || response.status === 403) {
      throw new UnauthorizedError('Failed to get app token', {
        text: response.statusText,
        result,
      });
    }
    throw new Error(`Failed to get app token: ${response.status} ${response.statusText}`);
  }

  const { accessToken, idToken } = convertTokenResponse(result);

  if (!accessToken) {
    logger.error("App token endpoint didn't return token", {
      result,
    });
    throw new Error("App token endpoint didn't return token");
  }

  try {
    window.sessionStorage.setItem(accessTokenStorageKey, accessToken);
    window.sessionStorage.setItem(idTokenStorageKey, idToken || '');
  } catch {
    /* empty */
  }
  logger.info('Successfully retrieved jwt token from application.');
  return { accessToken, idToken };
};

const isUsingNonVersionedSDK = (metadata: HandshakeMetadata): boolean => {
  const noExternalSDKVersion: boolean = !metadata.externalSDKVersion;
  const isLoadedWithScriptTag: boolean = !!metadata.sdkScriptUrl;
  const isLatestScriptTag: boolean = !!metadata.sdkScriptUrl?.includes(
    'https://connect.getvim.com/vim-os-sdk/vim-sdk.js',
  );

  return noExternalSDKVersion && isLoadedWithScriptTag && isLatestScriptTag;
};

type TokenResponse = {
  accessToken?: string;
  idToken?: string;
  token?: string;
  access_token?: string;
  id_token?: string;
};
